import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_REQUESTS_PAGE_SIZE,
  getAdminRequestsReadModel,
  archiveAdminRequest,
  archiveAdminRequestsBatch,
  restoreAdminRequestArchive,
  type AdminRequestCursor,
  type AdminRequestDirectoryItem,
  type AdminRequestsReadModel,
} from '../services/adminRequestsService';

export interface UseAdminRequestsState {
  data: AdminRequestsReadModel | null;
  requests: AdminRequestDirectoryItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  archiveRequest: (
    requestId: string,
    reasonCode: 'test_cleanup' | 'orphaned_service' | 'broken_legacy' | 'duplicate' | 'other',
    note?: string
  ) => Promise<boolean>;
  archiveRequestsBatch: (
    requestIds: string[],
    reasonCode: 'test_cleanup' | 'orphaned_service' | 'broken_legacy' | 'duplicate' | 'other',
    note?: string
  ) => Promise<boolean>;
  restoreRequest: (requestId: string, note?: string) => Promise<boolean>;
  mutationByRequestId: Record<string, 'archive' | 'restore' | undefined>;
  mutationErrorByRequestId: Record<string, string | undefined>;
}

const getErrorMessage = (error: unknown): string => {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'permission-denied') {
    return 'Brak uprawnień do odczytu repairRequests dla admina.';
  }
  if (code === 'failed-precondition') {
    return 'Firestore wymaga indeksu albo odrzucił query dla repairRequests.';
  }
  if (code === 'unavailable') {
    return 'Firestore jest chwilowo niedostępny. Spróbuj ponownie.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Nie udało się odczytać zgłoszeń.';
};

export const useAdminRequests = (): UseAdminRequestsState => {
  const [data, setData] = useState<AdminRequestsReadModel | null>(null);
  const [requests, setRequests] = useState<AdminRequestDirectoryItem[]>([]);
  const [cursor, setCursor] = useState<AdminRequestCursor>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mutationByRequestId, setMutationByRequestId] = useState<
    Record<string, 'archive' | 'restore' | undefined>
  >({});
  const [mutationErrorByRequestId, setMutationErrorByRequestId] = useState<
    Record<string, string | undefined>
  >({});

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const readModel = await getAdminRequestsReadModel(ADMIN_REQUESTS_PAGE_SIZE);
      setData(readModel);
      setRequests(readModel.requests);
      setCursor(readModel.cursor);
    } catch (readError) {
      console.error('[useAdminRequests] Requests read failed:', readError);
      setError(getErrorMessage(readError));
      setData(null);
      setRequests([]);
      setCursor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!data?.hasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const readModel = await getAdminRequestsReadModel(ADMIN_REQUESTS_PAGE_SIZE, cursor);
      setData((current) =>
        current
          ? {
              ...readModel,
              metrics: readModel.metrics,
              requests: [...requests, ...readModel.requests],
            }
          : readModel
      );
      setRequests((current) => [...current, ...readModel.requests]);
      setCursor(readModel.cursor);
    } catch (readError) {
      console.error('[useAdminRequests] Requests load more failed:', readError);
      setError(getErrorMessage(readError));
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, data?.hasMore, loadingMore, requests]);

  const runMutation = useCallback(
    async (
      requestId: string,
      kind: 'archive' | 'restore',
      task: () => Promise<unknown>
    ): Promise<boolean> => {
      setMutationByRequestId((current) => ({ ...current, [requestId]: kind }));
      setMutationErrorByRequestId((current) => ({ ...current, [requestId]: undefined }));
      try {
        await task();
        await refresh();
        return true;
      } catch (mutationError) {
        console.error(`[useAdminRequests] ${kind} failed:`, mutationError);
        setMutationErrorByRequestId((current) => ({
          ...current,
          [requestId]: getErrorMessage(mutationError),
        }));
        return false;
      } finally {
        setMutationByRequestId((current) => ({ ...current, [requestId]: undefined }));
      }
    },
    [refresh]
  );

  const archiveRequest = useCallback(
    async (
      requestId: string,
      reasonCode: 'test_cleanup' | 'orphaned_service' | 'broken_legacy' | 'duplicate' | 'other',
      note?: string
    ) => {
      return runMutation(requestId, 'archive', () =>
        archiveAdminRequest({ requestId, reasonCode, note })
      );
    },
    [runMutation]
  );

  const archiveRequestsBatch = useCallback(
    async (
      requestIds: string[],
      reasonCode: 'test_cleanup' | 'orphaned_service' | 'broken_legacy' | 'duplicate' | 'other',
      note?: string
    ) => {
      const batchKey = requestIds.slice().sort().join('|');
      return runMutation(batchKey, 'archive', () =>
        archiveAdminRequestsBatch({ requestIds, reasonCode, note })
      );
    },
    [runMutation]
  );

  const restoreRequest = useCallback(
    async (requestId: string, note?: string) => {
      return runMutation(requestId, 'restore', () =>
        restoreAdminRequestArchive({ requestId, note })
      );
    },
    [runMutation]
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    requests,
    loading,
    loadingMore,
    error,
    refresh,
    loadMore,
    archiveRequest,
    archiveRequestsBatch,
    restoreRequest,
    mutationByRequestId,
    mutationErrorByRequestId,
  };
};
